// Tracks the in-flight fetch for each (state, key) so concurrent callers share
// one request instead of each firing their own. Held outside the cached state
// object (keyed weakly by the store instance) so the promise never lands in
// persisted Pinia state and is collected when the store goes away.
const inFlight = new WeakMap();

function getInFlight(state, key) {
  const byKey = inFlight.get(state);
  return byKey ? byKey.get(key) : undefined;
}

function setInFlight(state, key, promise) {
  let byKey = inFlight.get(state);
  if (!byKey) {
    byKey = new Map();
    inFlight.set(state, byKey);
  }
  byKey.set(key, promise);
}

function clearInFlight(state, key) {
  const byKey = inFlight.get(state);
  if (byKey) {
    byKey.delete(key);
  }
}

export async function cacheFetch({ state, key, fetchFn, force = false, ttl }) {
  const now = Date.now();
  const value = state[key];
  const fetchedAt = state[`${key}FetchedAt`];
  const isStale = !fetchedAt || now - fetchedAt > ttl;

  if (!force && value && !isStale) {
    return value;
  }

  // Single-flight: while a fetch for this key is in flight, every caller shares
  // it. A route-guard probe and a background poll then collapse to one round
  // trip (and one 401 on session expiry) instead of a storm.
  const existing = getInFlight(state, key);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    try {
      const result = await fetchFn();
      state[key] = result;
      state[`${key}FetchedAt`] = now;
      return result;
    } catch (err) {
      state[key] = null;
      state[`${key}FetchedAt`] = null;
      throw err;
    } finally {
      clearInFlight(state, key);
    }
  })();

  setInFlight(state, key, promise);
  return promise;
}

export function newCache(cacheTtl = 5 * 60 * 1000) {
  return (args = {}) => {
    return cacheFetch({ ttl: cacheTtl, ...args });
  };
}
